import { useEffect, useRef } from 'react';
import { Renderer, Stave, StaveNote, Stem } from 'vexflow';
import { BOOM_FIGURE_VEX_DURATION } from '../lib/boomFigureMap';
import { ensureBoomVexFonts } from '../lib/boomVexFonts';
import type { NoteFigure } from '../types';

interface Props {
  figure: NoteFigure;
  size?: number;
  className?: string;
  color?: string;
  onFailed?: () => void;
}

function noteStyleForFigure(figure: NoteFigure, color: string) {
  if (figure === 'blanca' || figure === 'redonda') {
    return { fillStyle: 'transparent', strokeStyle: color };
  }
  return { fillStyle: color, strokeStyle: color };
}

/** Glifo VexFlow aislado para una figura rítmica. */
export function VexFigureIcon({
  figure,
  size = 44,
  className = '',
  color = '#2A2540',
  onFailed,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const failedRef = useRef(onFailed);
  failedRef.current = onFailed;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;

    (async () => {
      try {
        await ensureBoomVexFonts();
        if (cancelled) return;

        host.innerHTML = '';

        const canvasW = size;
        const canvasH = Math.round(size * 1.2);
        const staveY = Math.round(canvasH * 0.42);

        const renderer = new Renderer(host, Renderer.Backends.SVG);
        renderer.resize(canvasW, canvasH);
        const ctx = renderer.getContext();

        const stave = new Stave(0, staveY, canvasW);
        stave.setContext(ctx);

        const note = new StaveNote({
          keys: [figure === 'redonda' ? 'b/4' : 'f/4'],
          duration: BOOM_FIGURE_VEX_DURATION[figure],
          autoStem: true,
        });

        if (figure !== 'redonda') {
          note.setStemDirection(Stem.UP);
        }

        note.setStyle(noteStyleForFigure(figure, color));
        note.setStave(stave);
        note.draw();

        const svg = host.querySelector('svg');
        if (svg) {
          svg.style.display = 'block';
          svg.style.width = `${canvasW}px`;
          svg.style.height = `${canvasH}px`;
          svg.style.overflow = 'visible';
          svg.querySelectorAll('path, rect, ellipse, line').forEach((el) => {
            const node = el as SVGElement;
            if (figure === 'blanca' || figure === 'redonda') {
              node.setAttribute('fill', 'none');
              node.setAttribute('stroke', color);
            } else {
              node.setAttribute('fill', color);
              node.setAttribute('stroke', color);
            }
          });
        }
      } catch {
        if (!cancelled) failedRef.current?.();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [figure, size, color]);

  return (
    <div
      ref={hostRef}
      className={`vex-figure-icon vex-figure-icon--${figure} ${className}`.trim()}
      style={{ width: size, height: Math.round(size * 1.2) }}
      aria-hidden="true"
    />
  );
}
